import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import assessmentApi from '../assessmentApi';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import autoTable from 'jspdf-autotable';
import {
  applyAssessmentPdfBranding,
  drawPdfCenteredReportTitle,
  createA4Pdf,
  mergePdfAutoTableOptions,
  getEnovaLogoDataUrl,
  createAssessmentPdfPreview,
  downloadAssessmentPdfPreview,
  revokeAssessmentPdfPreview,
  AssessmentPdfFullscreenViewer,
  ExportExcelIcon,
  ExportPdfIcon,
  downloadStyledColumnTableExcel,
} from '../dashboard/downloadQuestionPaperPdf';
import { FiUser, FiSearch, FiFileText, FiUploadCloud, FiPlus, FiCheck, FiDownload, FiArrowLeft, FiTrash2, FiInbox, FiUsers, FiAward, FiX } from 'react-icons/fi';
import { AssessmentEditIcon, assessTipProps } from '../AssessmentContext';
import '../../../styles/Results.css';
import './HRUpload.css';

/** Must match downloadTemplate() header row exactly (order and names). */
const HR_UPLOAD_HEADERS = [
    'Candidate Name',
    'Batch Name',
    'Register Number',
    'Mobile Number',
    'Email ID',
];

const HR_UPLOAD_TEMPLATE_COLUMNS_LABEL = HR_UPLOAD_HEADERS.join(', ');

const normalizeHrUploadHeaderKey = (value) =>
    String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');

const validateHrUploadHeaderRow = (headerRow) => {
    const trimmed = headerRow.map((cell) => String(cell ?? '').trim());
    while (trimmed.length > 0 && trimmed[trimmed.length - 1] === '') {
        trimmed.pop();
    }

    if (trimmed.length !== HR_UPLOAD_HEADERS.length) {
        return {
            valid: false,
            error: `Invalid column count (${trimmed.length}). Use the downloaded template with exactly these columns in order: ${HR_UPLOAD_TEMPLATE_COLUMNS_LABEL}.`,
        };
    }

    const mismatches = [];
    trimmed.forEach((header, index) => {
        const expected = HR_UPLOAD_HEADERS[index];
        if (normalizeHrUploadHeaderKey(header) !== normalizeHrUploadHeaderKey(expected)) {
            mismatches.push(`column ${index + 1} must be "${expected}" (found "${header}")`);
        }
    });

    if (mismatches.length > 0) {
        return {
            valid: false,
            error: `Invalid column names. ${mismatches.join('; ')}. Download the template and keep the header row unchanged.`,
        };
    }

    return { valid: true };
};

const parseHrUploadCsvLine = (line) => {
    const result = [];
    let curValue = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
        const char = line[i];
        if (char === '"') {
            inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
            result.push(curValue.trim());
            curValue = '';
        } else {
            curValue += char;
        }
    }
    result.push(curValue.trim());
    return result;
};

const rowToHrCandidate = (cols) => {
    const cells = HR_UPLOAD_HEADERS.map((_, index) => String(cols[index] ?? '').trim());
    const student_name = cells[0];
    if (!student_name) return null;

    const rawPhone = cells[3] || '';
    let phone = rawPhone.replace(/[^0-9]/g, '');
    if (phone.length > 0) {
        const firstDigit = phone.charAt(0);
        if (!['9', '8', '7', '6'].includes(firstDigit)) {
            phone = '';
        }
    }
    if (phone.length > 10) {
        phone = phone.substring(0, 10);
    }

    const email = cells[4] || '';
    const emailError = (email && !email.includes('@')) ? 'Email Address must contain @' : '';
    const phoneError = (phone && phone.length !== 10) ? 'Mobile number must be exactly 10 digits' : '';

    return {
        id: null,
        keyId: `new-${Math.random().toString(36).substring(2, 9)}`,
        student_name,
        college_name: cells[1],
        register_number: cells[2],
        phone,
        email,
        isEditing: false,
        isNew: true,
        emailError,
        phoneError,
    };
};

const parseHrUploadTableRows = (tableRows) => {
    const dataRows = (tableRows || [])
        .map((row) => (Array.isArray(row) ? row : Object.values(row)).map((cell) => String(cell ?? '').trim()))
        .filter((row) => row.some((cell) => cell !== ''));

    if (dataRows.length === 0) {
        return { success: false, error: 'File is empty. Download the template and add candidates below the header row.' };
    }

    const headerCheck = validateHrUploadHeaderRow(dataRows[0]);
    if (!headerCheck.valid) {
        return { success: false, error: headerCheck.error };
    }

    const students = [];
    for (let i = 1; i < dataRows.length; i += 1) {
        const item = rowToHrCandidate(dataRows[i]);
        if (item) students.push(item);
    }

    if (students.length === 0) {
        return {
            success: false,
            error: 'No valid candidate rows found. Each row needs a value in the "Candidate Name" column.',
        };
    }

    return { success: true, students };
};

const parseHrUploadCsv = (text) => {
    const lines = String(text || '').split(/\r?\n/);
    const dataLines = lines
        .map((line) => line.trim())
        .filter((line) => line !== '');

    if (dataLines.length === 0) {
        return { success: false, error: 'File is empty. Download the template and add candidates below the header row.' };
    }

    const tableRows = dataLines.map((line) => parseHrUploadCsvLine(line));
    return parseHrUploadTableRows(tableRows);
};

const parseHrUploadExcel = (arrayBuffer) => {
    try {
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
            return { success: false, error: 'Excel file has no worksheets.' };
        }

        const sheet = workbook.Sheets[sheetName];
        const tableRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false });
        return parseHrUploadTableRows(tableRows);
    } catch (err) {
        console.error(err);
        return { success: false, error: 'Could not read Excel file. Use the downloaded .xlsx template.' };
    }
};

const HrExportFormatIconButton = ({ label, onClick, disabled, children }) => (
    <button
        type="button"
        className="export-format-icon-btn"
        onClick={onClick}
        disabled={disabled}
        title={label}
        aria-label={label}
    >
        {children}
        <span className="export-format-icon-btn__label">{label}</span>
    </button>
);

const HrExportPdfButton = ({ onClick, disabled, label = 'Download PDF' }) => (
    <HrExportFormatIconButton label={label} onClick={onClick} disabled={disabled}>
        <ExportPdfIcon />
    </HrExportFormatIconButton>
);

const HrExportExcelButton = ({ onClick, disabled }) => (
    <HrExportFormatIconButton label="Download Excel" onClick={onClick} disabled={disabled}>
        <ExportExcelIcon />
    </HrExportFormatIconButton>
);

const ExportReportColumnModal = ({
    isOpen,
    onClose,
    columns = [],
    selectedColumnIds = [],
    onToggleColumn,
    onToggleSelectAll,
    onExportExcel,
    onExportPdf,
    showPdfExport = true,
    pdfButtonLabel = 'Preview PDF',
    title = 'EXPORT REPORT',
    subtitle = 'CHOOSE FIELDS TO EXPORT',
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredColumns = useMemo(
        () =>
            columns.filter((col) =>
                col.label.toLowerCase().includes(searchTerm.trim().toLowerCase())
            ),
        [columns, searchTerm]
    );

    const selectedCount = selectedColumnIds.length;
    const allSelected = columns.length > 0 && selectedCount === columns.length;
    const noneSelected = selectedCount === 0;

    if (!isOpen) return null;

    return (
        <div
            className="assessment-popup-overlay"
            style={{ zIndex: 9999999 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="export-report-modal-title"
        >
            <div
                className="export-modal-v3 rounded-[24px] overflow-hidden w-[550px] max-w-[calc(100vw-2rem)] animate-scale-in shadow-2xl"
            >
                <div className="export-header-v3">
                    <div className="export-header-v3__leading">
                        <div className="export-icon-v3" aria-hidden>
                            <i className="bi bi-file-earmark-spreadsheet-fill" />
                        </div>
                        <div className="export-title-v3">
                            <h4 id="export-report-modal-title">{title}</h4>
                            <p>{subtitle}</p>
                        </div>
                    </div>
                    <div className="export-header-v3__actions">
                        <div className="export-format-actions export-format-actions--header">
                            {showPdfExport && onExportPdf && (
                                <HrExportPdfButton
                                    onClick={onExportPdf}
                                    disabled={noneSelected}
                                    label={pdfButtonLabel}
                                />
                            )}
                            {onExportExcel && (
                                <HrExportExcelButton onClick={onExportExcel} disabled={noneSelected} />
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="export-header-v3__close-btn"
                            aria-label="Close"
                            title="Close"
                        >
                            <i className="bi bi-x-lg" aria-hidden />
                        </button>
                    </div>
                </div>

                <div className="export-body-v3">
                    <div className="flex justify-between items-center mb-6 gap-4">
                        <div className="text-[13px] font-black text-slate-800 shrink-0">Select Columns</div>
                        <div className="export-search-v3 !mb-0 w-[180px] shrink-0">
                            <i className="bi bi-search" aria-hidden />
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                aria-label="Search columns"
                            />
                        </div>
                    </div>

                    <div className="export-grid-v3 max-h-[340px] overflow-y-auto custom-scrollbar pr-1">
                        {filteredColumns.length === 0 ? (
                            <p className="col-span-2 text-center text-[11px] font-bold text-slate-400 py-6">
                                No columns match your search.
                            </p>
                        ) : (
                            filteredColumns.map((col) => {
                                const isChecked = selectedColumnIds.includes(col.id);
                                return (
                                    <div
                                        key={col.id}
                                        className="export-item-v3"
                                        onClick={() => onToggleColumn(col.id)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onToggleColumn(col.id);
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        style={{
                                            background: isChecked ? '#fff5f5' : '#f8fafc',
                                            borderColor: isChecked ? '#fee2e2' : '#f1f5f9',
                                        }}
                                    >
                                        <span
                                            style={{
                                                background: isChecked ? '#b91c1c' : '#cbd5e1',
                                            }}
                                        >
                                            {isChecked && <i className="bi bi-check-lg" />}
                                        </span>
                                        {col.label}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="export-footer-actions-v3">
                        <button
                            type="button"
                            className="select-all-v3"
                            onClick={onToggleSelectAll}
                            aria-label={allSelected ? 'Deselect all columns' : 'Select all columns'}
                            title={allSelected ? 'Deselect all columns' : 'Select all columns'}
                        >
                            <span
                                className={`select-all-v3__box ${allSelected ? 'select-all-v3__box--checked' : ''}`}
                                aria-hidden
                            >
                                {allSelected && <i className="bi bi-check-lg text-[10px]" />}
                            </span>
                            <span className="select-all-v3__label">SELECT ALL</span>
                        </button>
                        <div className="selection-count-v3">
                            {selectedCount} OF {columns.length} SELECTED
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const HRUploadManager = ({ onBack }) => {
    const navigate = useNavigate();
    const batchNameInputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [colleges, setColleges] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [tests, setTests] = useState([]);
    const [students, setStudents] = useState([]);
    const [filteredColleges, setFilteredColleges] = useState([]);
    const [selectedCollege, setSelectedCollege] = useState(null);

    // Stats
    const [totalStudents, setTotalStudents] = useState(0);
    const [completedStudents, setCompletedStudents] = useState(0);

    // Form inputs
    const [templateName, setTemplateName] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [selectedTestId, setSelectedTestId] = useState('');

    // UI state
    const [activeTab, setActiveTab] = useState('bulk'); // 'bulk' or 'manual'
    const [collegeSearch, setCollegeSearch] = useState('');
    const [candidateSearch, setCandidateSearch] = useState('');
    const [manualRows, setManualRows] = useState([]);
    const [generatedCredentials, setGeneratedCredentials] = useState([]);

    // Export Report modal state
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportPdfPreview, setExportPdfPreview] = useState(null);
    const availableExportColumns = [
        { id: 'candidate_name', label: 'Candidate Name' },
        { id: 'college_name', label: 'Batch Name' },
        { id: 'register_number', label: 'Register Number' },
        { id: 'mobile_number', label: 'Mobile Number' },
        { id: 'email_id', label: 'Email ID' },
        { id: 'username', label: 'Username' },
        { id: 'password', label: 'Password' }
    ];
    const [selectedColumns, setSelectedColumns] = useState({
        candidate_name: true,
        college_name: true,
        register_number: true,
        mobile_number: true,
        email_id: true,
        username: true,
        password: true
    });

    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, type: null, data: null, message: '' });

    const hasUnsavedChanges = manualRows.some(r => r.isNew || r.isEditing || !r.id);
    const showCredentialColumns = manualRows.length > 0 && !hasUnsavedChanges;
    const isExistingBatchSelected = Boolean(selectedCollege?.template_name);

    useEffect(() => {
        if (!isExistingBatchSelected && batchNameInputRef.current) {
            batchNameInputRef.current.focus();
        }
    }, [isExistingBatchSelected]);

    const filteredManualRowEntries = useMemo(() => {
        const term = candidateSearch.trim().toLowerCase();
        return manualRows
            .map((row, idx) => ({ row, idx }))
            .filter(({ row }) => {
                if (!term) return true;
                return (row.student_name || '').toLowerCase().includes(term);
            });
    }, [manualRows, candidateSearch]);

    const generateCredentialPair = (studentName, regNumber, email) => {
        const username = email ? email.trim() : '';
        const cleanName = studentName ? studentName.replace(/[^a-zA-Z]/g, '') : '';
        let firstTwo = cleanName.substring(0, 2).toUpperCase();
        if (firstTwo.length < 2) {
            firstTwo = (firstTwo + 'XX').substring(0, 2);
        }
        const reg = (regNumber && regNumber.trim() !== '') ? regNumber.trim() : `REG${Math.floor(1000 + Math.random() * 9000)}`;
        const password = `${reg}_${firstTwo}`;
        return { username, password };
    };

    useEffect(() => {
        fetchInitialData();
        if (localStorage.getItem('hr_upload_force_new') === 'true') {
            localStorage.removeItem('hr_upload_force_new');
            setTemplateName('');
            setSelectedTemplateId('');
            setSelectedTestId('');
            setSelectedCollege(null);
            setGeneratedCredentials([]);
            setManualRows([]);
            setActiveTab('manual');
        }
    }, []);

    useEffect(() => {
        if (!showExportModal) {
            setExportPdfPreview((prev) => {
                revokeAssessmentPdfPreview(prev);
                return null;
            });
        }
    }, [showExportModal]);

    useEffect(() => {
        return () => {
            revokeAssessmentPdfPreview(exportPdfPreview);
        };
    }, [exportPdfPreview]);

    const clearExportPdfPreview = () => {
        setExportPdfPreview((prev) => {
            revokeAssessmentPdfPreview(prev);
            return null;
        });
    };

    useEffect(() => {
        if (!collegeSearch) {
            setFilteredColleges(colleges);
        } else {
            setFilteredColleges(colleges.filter(c => (c.template_name || '').toLowerCase().includes(collegeSearch.toLowerCase())));
        }
    }, [collegeSearch, colleges]);

    const fetchInitialData = async (currentCollege = selectedCollege) => {
        try {
            setLoading(true);
            const res = await assessmentApi.get('/assessment/hr-module/initial-data');
            if (res.data?.status === 'success') {
                const loadedColleges = res.data.colleges || [];
                setColleges(loadedColleges);
                setFilteredColleges(loadedColleges);
                setTotalStudents(res.data.total_students || 0);
                setCompletedStudents(res.data.completed_students || 0);
                setTemplates(res.data.templates || []);
                setTests(res.data.tests || []);
                const loadedStudents = res.data.students || [];
                setStudents(loadedStudents);

                if (currentCollege) {
                    const filtered = loadedStudents.filter(s => s.template_name === currentCollege.template_name);
                    setManualRows(filtered.map(s => {
                        const creds = generateCredentialPair(s.student_name, s.register_number, s.email || s.username);
                        const phoneVal = s.phone || s.mobile_number || '';
                        const emailVal = s.email || s.username || '';
                        const emailError = (emailVal && !emailVal.includes('@')) ? 'Email Address must contain @' : '';
                        const phoneError = (phoneVal && phoneVal.length !== 10) ? 'Mobile number must be exactly 10 digits' : '';
                        return {
                            id: s.id,
                            keyId: `db-${s.id}`,
                            student_name: s.student_name || '',
                            email: emailVal,
                            username: s.username || creds.username,
                            password: s.password || creds.password,
                            register_number: s.register_number || '',
                            phone: phoneVal,
                            college_name: s.college_name || '',
                            isEditing: false,
                            isNew: false,
                            emailError,
                            phoneError,
                        };
                    }));
                    if (!templateName && currentCollege.template_name) {
                        setTemplateName(currentCollege.template_name);
                    }
                } else {
                    setManualRows([]);
                }
            }
        } catch (err) {
            console.error('Error fetching initial data:', err);
            toast.error('Failed to fetch initial module data');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterCollege = async (collegeObj) => {
        setSelectedCollege(collegeObj);
        setTemplateName(collegeObj.template_name);
        try {
            setLoading(true);
            const res = await assessmentApi.get(`/assessment/hr-module/students?template=${encodeURIComponent(collegeObj.template_name)}`);
            if (res.data?.status === 'success') {
                const loadedStudents = res.data.students || [];
                setStudents(loadedStudents);
                setManualRows(loadedStudents.map(s => {
                    const creds = generateCredentialPair(s.student_name, s.register_number, s.email || s.username);
                    const phoneVal = s.phone || s.mobile_number || '';
                    const emailVal = s.email || s.username || '';
                    const emailError = (emailVal && !emailVal.includes('@')) ? 'Email Address must contain @' : '';
                    const phoneError = (phoneVal && phoneVal.length !== 10) ? 'Mobile number must be exactly 10 digits' : '';
                    return {
                        id: s.id,
                        keyId: `db-${s.id}`,
                        student_name: s.student_name || '',
                        email: emailVal,
                        username: s.username || creds.username,
                        password: s.password || creds.password,
                        register_number: s.register_number || '',
                        phone: phoneVal,
                        college_name: s.college_name || '',
                        isEditing: false,
                        isNew: false,
                        emailError,
                        phoneError,
                    };
                }));
                setActiveTab('manual');
                setGeneratedCredentials([]);
            }
        } catch (err) {
            console.error('Error filtering candidates:', err);
            toast.error('Failed to filter candidate records');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCollege = async (e, collegeObj) => {
        e.stopPropagation();
        setDeleteConfirm({
            isOpen: true,
            type: 'college',
            data: collegeObj,
            message: `Are you sure you want to delete template '${collegeObj.template_name}' and all its uploaded candidate records? This action cannot be undone.`
        });
    };

    const executeDeleteCollege = async (collegeObj) => {
        try {
            setLoading(true);
            const res = await assessmentApi.delete(`/assessment/hr-module/college/${encodeURIComponent(collegeObj.template_name)}`);
            if (res.data?.status === 'success') {
                toast.success(res.data.message || 'Template deleted successfully');
                if (selectedCollege?.template_name === collegeObj.template_name || templateName === collegeObj.template_name) {
                    setSelectedCollege(null);
                    setTemplateName('');
                    setManualRows([]);
                }
                await fetchInitialData(null);
            } else {
                toast.error(res.data?.message || 'Failed to delete college');
            }
        } catch (err) {
            console.error('Error deleting college:', err);
            toast.error('Error deleting college records');
        } finally {
            setLoading(false);
            setDeleteConfirm({ isOpen: false, type: null, data: null, message: '' });
        }
    };

    const handleNewUpload = () => {
        setTemplateName('');
        setSelectedTemplateId('');
        setSelectedTestId('');
        setSelectedCollege(null);
        setGeneratedCredentials([]);
        setManualRows([]);
        setActiveTab('manual');
    };

    const downloadTemplate = () => {
        const headers = [HR_UPLOAD_HEADERS];
        const sample = [['John Doe', 'Batch A', 'REG100234', '9876543210', 'john.doe@example.com']];
        const ws = XLSX.utils.aoa_to_sheet([...headers, ...sample]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'CandidateTemplate');
        XLSX.writeFile(wb, 'HR_Candidate_Upload_Template.xlsx');
        toast.success('Excel Template downloaded successfully');
    };

    const handleBulkUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const isExcel = /\.(xlsx|xls)$/i.test(file.name);
        const isCsv = /\.csv$/i.test(file.name);
        if (!isExcel && !isCsv) {
            toast.error('Please upload a .xlsx, .xls, or .csv file from the downloaded template.');
            e.target.value = null;
            return;
        }

        const reader = new FileReader();
        reader.onload = (evt) => {
            const result = isExcel
                ? parseHrUploadExcel(evt.target.result)
                : parseHrUploadCsv(evt.target.result);

            if (!result.success) {
                toast.error(result.error);
                return;
            }

            setManualRows((prev) => [...result.students, ...prev]);
            setActiveTab('manual');
            toast.success(
                `Successfully loaded ${result.students.length} candidate record(s). Click "Save" below to save to repository.`
            );
        };

        if (isExcel) {
            reader.readAsArrayBuffer(file);
        } else {
            reader.readAsText(file);
        }
        e.target.value = null;
    };
    const addManualRow = () => {
        const creds = generateCredentialPair('', '', '');
        setManualRows([{
            id: null,
            keyId: `new-${Math.random().toString(36).substring(2, 9)}`,
            student_name: '',
            email: '',
            username: creds.username,
            password: creds.password,
            register_number: '',
            phone: '',
            college_name: '',
            isEditing: true,
            isNew: true,
            emailError: '',
            phoneError: ''
        }, ...manualRows]);
        setActiveTab('manual');
        setGeneratedCredentials([]);
    };

    const updateManualRow = (index, field, val) => {
        const updated = [...manualRows];
        if (field === 'phone') {
            // Only allow numbers
            let filtered = val.replace(/[^0-9]/g, '');
            if (filtered.length > 0) {
                const firstDigit = filtered.charAt(0);
                if (!['9', '8', '7', '6'].includes(firstDigit)) {
                    // Do not allow this character if it's the first character and not 9, 8, 7, or 6
                    return;
                }
            }
            if (filtered.length > 10) {
                filtered = filtered.substring(0, 10);
            }
            updated[index][field] = filtered;

            if (filtered.length > 0 && filtered.length !== 10) {
                updated[index].phoneError = 'Mobile number must be exactly 10 digits';
            } else {
                updated[index].phoneError = '';
            }
        } else if (field === 'email') {
            updated[index][field] = val;
            const creds = generateCredentialPair(updated[index].student_name, updated[index].register_number, updated[index].email);
            updated[index].username = creds.username;
            updated[index].password = creds.password;

            if (val && !val.includes('@')) {
                updated[index].emailError = 'Email Address must contain @';
            } else {
                updated[index].emailError = '';
            }
        } else {
            updated[index][field] = val;
            if (['student_name', 'register_number'].includes(field)) {
                const creds = generateCredentialPair(updated[index].student_name, updated[index].register_number, updated[index].email);
                updated[index].username = creds.username;
                updated[index].password = creds.password;
            }
        }
        setManualRows(updated);
    };

    const startEditManualRow = (index) => {
        const updated = [...manualRows];
        updated[index].isEditing = true;
        setManualRows(updated);
    };

    const cancelManualRow = (index) => {
        const row = manualRows[index];
        if (row.isNew) {
            const updated = manualRows.filter((_, idx) => idx !== index);
            setManualRows(updated);
        } else {
            const updated = [...manualRows];
            updated[index].isEditing = false;
            const original = students.find(s => s.id === row.id);
            if (original) {
                updated[index].student_name = original.student_name || '';
                updated[index].email = original.email || original.username || '';
                updated[index].username = original.username || original.email || '';
                updated[index].password = original.password || '';
                updated[index].register_number = original.register_number || '';
                updated[index].phone = original.phone || '';
                updated[index].college_name = original.college_name || '';
                updated[index].emailError = (original.email && !original.email.includes('@')) ? 'Email Address must contain @' : '';
                updated[index].phoneError = (original.phone && original.phone.length !== 10) ? 'Mobile number must be exactly 10 digits' : '';
            }
            setManualRows(updated);
        }
    };

    const saveManualRow = (index) => {
        const row = manualRows[index];
        if (!row.student_name?.trim()) {
            toast.error('Candidate Name is required');
            return;
        }
        if (!row.email?.trim()) {
            toast.error('Email ID / Username is required');
            return;
        }
        if (row.email && !row.email.includes('@')) {
            toast.error('Email Address must contain @');
            return;
        }
        if (row.phone && row.phone.length !== 10) {
            toast.error('Mobile number must be exactly 10 digits');
            return;
        }

        const creds = generateCredentialPair(row.student_name, row.register_number, row.email);

        const updated = [...manualRows];
        updated[index] = {
            ...row,
            student_name: row.student_name.trim(),
            email: row.email.trim(),
            username: creds.username,
            password: creds.password,
            register_number: row.register_number?.trim() || '',
            phone: row.phone?.trim() || '',
            college_name: row.college_name?.trim() || '',
            isEditing: false,
            emailError: '',
            phoneError: ''
        };
        setManualRows(updated);
    };

    const deleteManualRowRecord = async (index, id) => {
        if (!id) {
            const updated = manualRows.filter((_, idx) => idx !== index);
            setManualRows(updated);
            return;
        }

        setDeleteConfirm({
            isOpen: true,
            type: 'student',
            data: { index, id },
            message: `Are you sure you want to delete ${manualRows[index].student_name}?`
        });
    };

    const executeDeleteStudent = async (index, id) => {
        try {
            setLoading(true);
            const res = await assessmentApi.delete(`/assessment/hr-module/student/${id}`);
            if (res.data?.status === 'success') {
                toast.success(res.data.message ? res.data.message.replace(/Student/g, 'Candidate').replace(/student/g, 'candidate') : 'Candidate deleted successfully');
                const updated = manualRows.filter((_, idx) => idx !== index);
                setManualRows(updated);

                assessmentApi.get('/assessment/hr-module/initial-data').then(initRes => {
                    if (initRes.data?.status === 'success') {
                        setColleges(initRes.data.colleges || []);
                        setFilteredColleges(initRes.data.colleges || []);
                        setTotalStudents(initRes.data.total_students || 0);
                        setCompletedStudents(initRes.data.completed_students || 0);
                        setStudents(initRes.data.students || []);
                    }
                }).catch(e => console.error(e));
            } else {
                toast.error(res.data?.message ? res.data.message.replace(/Student/g, 'Candidate').replace(/student/g, 'candidate') : 'Failed to delete candidate');
            }
        } catch (err) {
            console.error('Error deleting candidate:', err);
            toast.error(err.response?.data?.message ? err.response.data.message.replace(/Student/g, 'Candidate').replace(/student/g, 'candidate') : 'Error deleting candidate record');
        } finally {
            setLoading(false);
            setDeleteConfirm({ isOpen: false, type: null, data: null, message: '' });
        }
    };

    const handleConfirmDelete = () => {
        if (deleteConfirm.type === 'college') {
            executeDeleteCollege(deleteConfirm.data);
        } else if (deleteConfirm.type === 'student') {
            executeDeleteStudent(deleteConfirm.data.index, deleteConfirm.data.id);
        }
    };

    const handleMasterSave = async () => {
        if (!templateName?.trim()) {
            toast.error('Please enter or select a Batch Name before saving.');
            return false;
        }

        const isDuplicate = colleges.some(c => 
            c.template_name?.trim().toLowerCase() === templateName.trim().toLowerCase()
        );
        if (isDuplicate && (!selectedCollege || selectedCollege.template_name?.trim().toLowerCase() !== templateName.trim().toLowerCase())) {
            toast.error('A batch with this name already exists. Please choose a unique name.');
            return false;
        }

        const validRows = manualRows.filter(r => r.student_name && r.student_name.trim() !== '');
        if (validRows.length === 0) {
            toast.error('No candidate records found to save. Please add candidate details.');
            return false;
        }

        for (const row of validRows) {
            if (!row.email?.trim()) {
                toast.error(`Please provide an Email ID / Username for candidate: ${row.student_name}`);
                return false;
            }
            if (row.email && !row.email.includes('@')) {
                toast.error(`Email Address for candidate '${row.student_name}' must contain @`);
                return false;
            }
            if (row.phone && row.phone.length !== 10) {
                toast.error(`Mobile number for candidate '${row.student_name}' must be exactly 10 digits`);
                return false;
            }
            if (row.emailError || row.phoneError) {
                toast.error(`Please correct validation errors for candidate '${row.student_name}'`);
                return false;
            }
        }

        return await submitUploadRecords(validRows);
    };

    const submitUploadRecords = async (records) => {
        const payload = {
            template_name: templateName,
            test_id: selectedTestId ? parseInt(selectedTestId) : null,
            students: records.map(r => {
                const creds = generateCredentialPair(r.student_name, r.register_number, r.email);
                return {
                    id: r.id || null,
                    student_name: r.student_name,
                    email: r.email,
                    username: r.username || creds.username,
                    password: r.password || creds.password,
                    register_number: r.register_number || '',
                    phone: r.phone || '',
                    college_name: r.college_name?.trim() || ''
                };
            })
        };

        try {
            setLoading(true);
            const res = await assessmentApi.post('/assessment/hr-module/upload', payload);
            if (res.data?.status === 'success') {
                toast.success(res.data.message ? res.data.message.replace(/Student/g, 'Candidate').replace(/student/g, 'candidate') : 'Entire upload batch successfully saved to repository!');
                setGeneratedCredentials(res.data.credentials || []);
                const nextCollege = selectedCollege || { template_name: templateName };
                setSelectedCollege(nextCollege);
                await fetchInitialData(nextCollege);
                return true;
            } else {
                toast.error(res.data?.message ? res.data.message.replace(/Student/g, 'Candidate').replace(/student/g, 'candidate') : 'Failed to upload candidates');
                return false;
            }
        } catch (err) {
            console.error('Error submitting upload:', err);
            toast.error(err.response?.data?.message ? err.response.data.message.replace(/Student/g, 'Candidate').replace(/student/g, 'candidate') : 'Error processing candidate upload');
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (hasUnsavedChanges) {
            const success = await handleMasterSave();
            if (success) {
                setTimeout(() => {
                    if (onBack) {
                        onBack();
                    } else {
                        navigate('/assessment');
                    }
                }, 800);
            }
        } else {
            if (onBack) {
                onBack();
            } else {
                navigate('/assessment');
            }
        }
    };

    const generateFullUploadReportExcel = () => {
        const sourceData = generatedCredentials.length > 0 ? generatedCredentials : manualRows;
        if (!sourceData || sourceData.length === 0) {
            toast.error('No candidate records available to generate report');
            return;
        }
        setShowExportModal(true);
    };

    const handleToggleColumn = (id) => {
        setSelectedColumns(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const handleSelectAllColumns = () => {
        const allOn = availableExportColumns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {});
        setSelectedColumns(allOn);
    };

    const handleDeselectAllColumns = () => {
        const allOff = availableExportColumns.reduce((acc, col) => ({ ...acc, [col.id]: false }), {});
        setSelectedColumns(allOff);
    };

    const selectedColumnIds = useMemo(
        () => availableExportColumns.filter((col) => selectedColumns[col.id]).map((col) => col.id),
        [selectedColumns]
    );

    const handleToggleSelectAllColumns = () => {
        if (selectedColumnIds.length === availableExportColumns.length) {
            handleDeselectAllColumns();
        } else {
            handleSelectAllColumns();
        }
    };

    const buildHrExportTableData = () => {
        const selectedColObjs = availableExportColumns.filter((c) => selectedColumns[c.id]);
        if (selectedColObjs.length === 0) {
            return null;
        }

        const sourceData = generatedCredentials.length > 0 ? generatedCredentials : manualRows;
        const headerLabels = selectedColObjs.map((c) => c.label);
        const rows = sourceData.map((s) => {
            const creds = generateCredentialPair(s.student_name, s.register_number, s.email || s.username);
            const dataMap = {
                candidate_name: s.student_name || '-',
                college_name: s.college_name || '-',
                register_number: s.register_number || '-',
                mobile_number: s.phone || s.mobile_number || '-',
                email_id: s.email || s.username || '-',
                username: s.username || creds.username,
                password: s.password || creds.password,
            };
            return selectedColObjs.map((c) => dataMap[c.id]);
        });

        const reportBaseName = templateName || selectedCollege?.template_name || 'Export';
        return { selectedColObjs, headerLabels, rows, reportBaseName };
    };

    const executeExportReportExcel = async () => {
        const exportData = buildHrExportTableData();
        if (!exportData) {
            toast.error('Please select at least one column to include in the report');
            return;
        }

        const { headerLabels, rows, reportBaseName } = exportData;
        try {
            await downloadStyledColumnTableExcel({
                filename: `Candidate_Report_${reportBaseName}_${Date.now()}.xlsx`,
                sheetName: 'CandidateReport',
                headerLabels,
                rows,
            });
            toast.success('Excel report generated successfully');
            setShowExportModal(false);
        } catch (err) {
            toast.error(err?.message || 'Failed to export Excel report');
        }
    };

    const buildHrCandidateReportPdfDoc = async (exportData) => {
        const { headerLabels, rows, reportBaseName, selectedColObjs } = exportData;
        const colCount = headerLabels.length;
        const compact =
            colCount > 10
                ? { fontSize: 6, cellPadding: 1 }
                : colCount > 7
                  ? { fontSize: 7, cellPadding: 1.5 }
                  : { fontSize: 8, cellPadding: 2 };
        const doc = createA4Pdf('portrait');
        const tableStartY = drawPdfCenteredReportTitle(doc, 'CANDIDATE REPORT');
        autoTable(
            doc,
            mergePdfAutoTableOptions(doc, {
                startY: tableStartY,
                head: [headerLabels.map((h) => String(h).toUpperCase())],
                body: rows,
                showHead: 'everyPage',
                styles: { overflow: 'linebreak', ...compact },
                headStyles: { fillColor: [185, 28, 28], textColor: 255, fontStyle: 'bold', ...compact },
            })
        );

        const logoDataUrl = await getEnovaLogoDataUrl();
        applyAssessmentPdfBranding(doc, logoDataUrl);

        const filename = `Candidate_Report_${reportBaseName}_${Date.now()}.pdf`;
        return { doc, filename };
    };

    const executeExportReportPdf = async () => {
        const exportData = buildHrExportTableData();
        if (!exportData) {
            toast.error('Please select at least one column to include in the report');
            return;
        }

        try {
            const { doc, filename } = await buildHrCandidateReportPdfDoc(exportData);
            setExportPdfPreview((prev) => {
                revokeAssessmentPdfPreview(prev);
                return createAssessmentPdfPreview(doc, filename);
            });
        } catch (err) {
            console.error('PDF export failed:', err);
            toast.error(err?.message || 'Failed to generate PDF report');
        }
    };

    const handleDownloadExportPdfPreview = () => {
        downloadAssessmentPdfPreview(exportPdfPreview);
        toast.success('PDF report downloaded successfully');
    };

    return (
        <>
            <AssessmentPdfFullscreenViewer
                preview={exportPdfPreview}
                onClose={clearExportPdfPreview}
                onDownload={handleDownloadExportPdfPreview}
            />
            <div className="hr-upload-page assess-mod-page flex flex-col h-[calc(100vh-7.5rem)] min-h-0 bg-[#f1f5f9] overflow-hidden font-sans relative">
                <header className="hr-upload-header h-[70px] bg-white border-b border-slate-200 flex items-center justify-between px-10 shrink-0">
                    <button
                        type="button"
                        onClick={() => (onBack ? onBack() : navigate('/assessment'))}
                        className="hr-upload-back-mobile lg:hidden"
                        {...assessTipProps('Go back')}
                    >
                        <FiArrowLeft className="text-lg" />
                    </button>
                    <div className="hr-upload-header-brand flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#b91c1c] rounded-xl flex items-center justify-center shadow-lg shadow-red-100">
                            <FiUser className="text-white text-xl" />
                        </div>
                        <div>
                            <h1 className="text-[16px] font-black text-[#1e293b] tracking-tight leading-none mb-1">
                                External Setup
                            </h1>
                            <p className="text-[9px] text-slate-400 font-black uppercase tracking-[0.2em]">
                                Candidates Onboarding & Repository
                            </p>
                        </div>
                    </div>
                    <div className="hr-upload-header-actions flex items-center gap-3">
                        {selectedCollege && (
                            <button
                                onClick={handleNewUpload}
                                className="btn-assessment btn-assessment-danger"
                            >
                                <FiPlus className="text-sm" /> New Upload
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => (onBack ? onBack() : navigate('/assessment'))}
                            className="btn-assessment btn-assessment-danger hidden lg:inline-flex"
                        >
                            <FiArrowLeft className="text-sm" /> Back
                        </button>
                    </div>
                </header>

                <div className="hr-upload-body flex flex-1 min-h-0 overflow-hidden">
                    <aside className="hr-upload-sidebar w-full lg:w-[300px] bg-[#f8fafc] border-e border-slate-200 flex flex-col shrink-0 overflow-hidden">
                        <div className="hr-upload-sidebar-search bg-white p-6 border-b border-slate-200 shrink-0">
                            <div className="relative">
                                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />
                                <input
                                    type="text"
                                    placeholder="Search batches..."
                                    value={collegeSearch}
                                    onChange={(e) => setCollegeSearch(e.target.value)}
                                    className="w-full bg-[#f1f5f9] border border-slate-200 rounded-xl pl-10 pr-4 py-2 text-[12px] font-bold focus:ring-4 focus:ring-red-50 transition-all placeholder:text-slate-400 shadow-inner outline-none"
                                />
                            </div>
                        </div>

                        <div className="hr-upload-sidebar-list flex-1 p-6 overflow-y-auto custom-scrollbar">
                            <div className="space-y-3">
                            {filteredColleges.map((c, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => handleFilterCollege(c)}
                                    className={`relative p-3 rounded-[24px] border border-red-100 cursor-pointer transition-all group flex items-center gap-3 overflow-hidden ${selectedCollege?.template_name === c.template_name ? 'bg-red-50 shadow-sm' : 'bg-white hover:border-red-200'}`}
                                >
                                    <div className="w-10 h-10 bg-white border border-slate-100 rounded-xl flex items-center justify-center text-[#b91c1c] shadow-sm shrink-0">
                                        <FiUsers className="text-lg" />
                                    </div>
                                    <div className="flex-1 min-w-0 pr-2">
                                        <h5 className={`text-[13px] font-black mb-0 tracking-tight truncate ${selectedCollege?.template_name === c.template_name ? 'text-[#b91c1c]' : 'text-[#1e293b]'}`}>
                                            {c.template_name}
                                        </h5>
                                        <p className="text-[8px] text-slate-400 font-black uppercase tracking-widest mt-0.5 truncate">
                                            {c.student_count || 0} CANDIDATES
                                        </p>
                                    </div>
                                    <div
                                        className="hr-upload-template-delete absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all bg-white/95 backdrop-blur-sm pl-4 py-2 rounded-l-xl z-10"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <button
                                            onClick={(e) => handleDeleteCollege(e, c)}
                                            title="Delete batch"
                                            className="w-8 h-8 bg-red-50 text-red-500 rounded-lg flex items-center justify-center border border-red-100 hover:bg-[#b91c1c] hover:text-white transition-all shadow-sm"
                                        >
                                            <FiTrash2 className="text-[10px]" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            </div>
                        </div>
                    </aside>

                    {/* Main Content Area Wrapper */}
                    <div className="hr-upload-main-wrap flex-1 min-w-0 min-h-0 p-6 lg:p-10 flex flex-col overflow-hidden">
                        <div className="hr-upload-card flex-1 min-h-0 bg-white rounded-[32px] border border-slate-100 shadow-sm flex flex-col overflow-hidden p-6 sm:p-8 max-w-[1600px] w-full mx-auto">
                            <div className="shrink-0">
                                {/* Top Form Controls Row */}
                                <div className="hr-upload-form-row flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pb-8 border-b border-slate-100">
                                    <div className="flex items-center gap-5 flex-1 max-w-lg w-full">
                                        <div className="w-14 h-14 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center text-2xl shrink-0 shadow-inner">
                                            <FiFileText />
                                        </div>
                                        <div className="space-y-1 text-left flex-1 min-w-0">
                                            <div className="flex flex-col ml-1">
                                                <span className="text-xs font-black text-slate-800 uppercase tracking-tight">Batch Name</span>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                    {isExistingBatchSelected
                                                        ? 'Selected batch (read only)'
                                                        : 'Enter batch name for new upload'}
                                                </span>
                                            </div>
                                            <input
                                                ref={batchNameInputRef}
                                                type="text"
                                                value={templateName}
                                                onChange={(e) => {
                                                    if (isExistingBatchSelected) return;
                                                    setTemplateName(e.target.value);
                                                }}
                                                readOnly={isExistingBatchSelected}
                                                placeholder="Enter Batch Name..."
                                                aria-readonly={isExistingBatchSelected}
                                                className={`w-full border rounded-2xl p-3.5 text-sm font-bold outline-none transition-all shadow-sm truncate ${
                                                    isExistingBatchSelected
                                                        ? 'bg-slate-100 border-slate-200 text-slate-600 cursor-default'
                                                        : 'bg-slate-50/80 border-slate-100 text-slate-800 focus:border-red-400 focus:bg-white'
                                                }`}
                                            />
                                        </div>
                                    </div>

                                    <div className="hr-upload-actions-row assess-mod-actions-row flex flex-wrap items-center gap-3 w-full sm:w-auto justify-start sm:justify-end">
                                        <button
                                            type="button"
                                            onClick={downloadTemplate}
                                            className="btn-assessment btn-assessment-outline-purple !py-2.5 !px-6 shrink-0"
                                        >
                                            <FiDownload className="text-sm" /> Template
                                        </button>

                                        <div className="hr-upload-bulk-wrap relative shrink-0">
                                            <input
                                                type="file"
                                                id="bulk-upload-excel"
                                                accept=".xlsx, .xls, .csv"
                                                onChange={handleBulkUpload}
                                                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
                                            />
                                            <button
                                                type="button"
                                                className="btn-assessment btn-assessment-outline-purple !py-2.5 !px-6 cursor-pointer relative z-0 shrink-0 w-full"
                                            >
                                                <FiUploadCloud className="text-sm" /> Bulk Upload
                                            </button>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={addManualRow}
                                            className="btn-assessment btn-assessment-danger !py-2.5 !px-6 shrink-0"
                                        >
                                            <FiPlus className="text-sm" /> Add Candidate
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Content Area Based on Active Tab */}
                            <div className="hr-upload-card-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden custom-scrollbar pt-6">
                                    {/* Success Generated Credentials View */}
                                    {generatedCredentials.length > 0 ? (
                                        <div className="space-y-6 animate-in fade-in duration-300 text-left pb-6">
                                            <div className="flex items-center justify-end mb-4">
                                                <button
                                                    onClick={generateFullUploadReportExcel}
                                                    className="bg-emerald-600 text-white px-4 py-2.5 rounded-xl font-black uppercase tracking-widest text-[11px] flex items-center gap-2 hover:bg-emerald-700 shadow-sm shadow-emerald-200 active:scale-95 transition-all shrink-0"
                                                >
                                                    <FiDownload className="text-sm" /> Export Student Details
                                                </button>
                                            </div>

                                            <div className="hr-upload-table-wrap border border-slate-100 rounded-3xl overflow-hidden shadow-sm overflow-x-auto custom-scrollbar">
                                                <table className="hr-upload-data-table w-full text-left whitespace-nowrap min-w-[950px]">
                                                    <thead className="bg-slate-50/80 border-b border-slate-100">
                                                        <tr>
                                                            <th className="px-5 py-4 text-xs font-black text-slate-400 uppercase tracking-wider">#</th>
                                                            <th className="px-5 py-4 text-xs font-black text-slate-400 uppercase tracking-wider">Candidate Name</th>
                                                            <th className="px-5 py-4 text-xs font-black text-slate-400 uppercase tracking-wider">Register Number</th>
                                                            <th className="px-5 py-4 text-xs font-black text-slate-400 uppercase tracking-wider">Username (Email ID)</th>
                                                            <th className="px-5 py-4 text-xs font-black text-slate-400 uppercase tracking-wider">Password</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-50">
                                                        {generatedCredentials.map((cred, idx) => (
                                                            <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                                                <td className="px-5"><span className="hr-upload-cell-data">{idx + 1}</span></td>
                                                                <td className="px-5"><span className="hr-upload-cell-data">{cred.student_name}</span></td>
                                                                <td className="px-5"><span className="hr-upload-cell-data">{cred.register_number || '-'}</span></td>
                                                                <td className="px-5"><span className="hr-upload-cell-data px-3 py-1.5 bg-slate-100 rounded-xl shadow-inner inline-block">{cred.username}</span></td>
                                                                <td className="px-5"><span className="hr-upload-password-pill px-3 py-1.5 bg-red-50 rounded-lg border border-red-200 shadow-inner inline-block">{cred.password}</span></td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : activeTab === 'manual' && manualRows.length > 0 ? (
                                        <div className="space-y-6 animate-in fade-in duration-300 text-left pb-6">
                                            <div className="hr-upload-section-head flex items-center justify-between border-b border-slate-100 pb-4">
                                                <h4 className="text-sm font-black text-slate-800 uppercase tracking-widest flex items-center gap-3">
                                                    <span className="w-2.5 h-6 bg-red-600 rounded-full"></span> {selectedCollege ? `${selectedCollege.template_name} Candidates` : 'Candidate Management'} ({candidateSearch.trim() ? `${filteredManualRowEntries.length} / ${manualRows.length}` : manualRows.length})
                                                </h4>
                                                <div className="hr-upload-section-actions flex items-center gap-3 flex-1 justify-end min-w-0">
                                                    <div className="relative flex-1 max-w-xs min-w-[180px]">
                                                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none" />
                                                        <input
                                                            type="text"
                                                            value={candidateSearch}
                                                            onChange={(e) => setCandidateSearch(e.target.value)}
                                                            placeholder="Search Candidate Name..."
                                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-3 py-2.5 text-xs font-bold text-slate-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-50 transition-all placeholder:text-slate-400"
                                                        />
                                                    </div>
                                                    <button
                                                        onClick={generateFullUploadReportExcel}
                                                        className="bg-emerald-600 text-white px-4 py-2.5 rounded-xl font-black uppercase tracking-widest text-[11px] flex items-center gap-2 hover:bg-emerald-700 shadow-sm shadow-emerald-200 transition-all active:scale-95 shrink-0"
                                                    >
                                                        <FiDownload className="text-sm" /> Export Student Details
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="hr-upload-table-wrap border border-slate-100 rounded-3xl overflow-hidden shadow-sm overflow-x-auto custom-scrollbar">
                                                <table className="hr-upload-data-table w-full text-left whitespace-nowrap min-w-[1100px]">
                                                    <thead className="bg-slate-50/80 border-b border-slate-100">
                                                        <tr>
                                                            <th className="px-4 py-4 text-xs font-black text-slate-400 uppercase tracking-wider w-12">#</th>
                                                            <th className="px-4 py-4 text-xs font-black text-slate-400 uppercase tracking-wider">Candidate Name</th>
                                                            <th className="px-4 py-4 text-xs font-black text-slate-400 uppercase tracking-wider">Batch Name</th>
                                                            <th className="px-4 py-4 text-xs font-black text-slate-400 uppercase tracking-wider">Register Number</th>
                                                            <th className="px-4 py-4 text-xs font-black text-slate-400 uppercase tracking-wider">Mobile Number</th>
                                                            <th className="px-4 py-4 text-xs font-black text-slate-400 uppercase tracking-wider">Email ID</th>
                                                            {showCredentialColumns && (
                                                                <>
                                                                    <th className="px-4 py-4 text-xs font-black text-slate-400 uppercase tracking-wider">Username</th>
                                                                    <th className="px-4 py-4 text-xs font-black text-slate-400 uppercase tracking-wider">Password</th>
                                                                </>
                                                            )}
                                                            <th className="px-4 py-4 text-xs font-black text-slate-400 uppercase tracking-wider text-center w-24">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-slate-50">
                                                        {filteredManualRowEntries.length === 0 ? (
                                                            <tr>
                                                                <td colSpan={showCredentialColumns ? 9 : 7} className="px-6 py-12 text-center text-xs font-bold text-slate-400 uppercase tracking-widest">
                                                                    No candidates match your search
                                                                </td>
                                                            </tr>
                                                        ) : filteredManualRowEntries.map(({ row, idx }, displayIdx) => (
                                                            <tr key={row.keyId || (row.id ? `db-${row.id}` : `new-idx-${idx}`)} className={`transition-colors ${row.isEditing ? 'bg-red-50/20' : 'hover:bg-slate-50/50'}`}>
                                                                <td className="px-4"><span className="hr-upload-cell-data">{displayIdx + 1}</span></td>
                                                                <td className="px-4">
                                                                    {row.isEditing ? (
                                                                        <input
                                                                            type="text"
                                                                            value={row.student_name}
                                                                            onChange={(e) => updateManualRow(idx, 'student_name', e.target.value)}
                                                                            placeholder="Candidate Name"
                                                                            className="hr-upload-cell-input w-full p-2.5 bg-white border border-slate-100 focus:border-red-400 focus:ring-2 focus:ring-red-50 rounded-xl outline-none transition-all shadow-sm"
                                                                        />
                                                                    ) : (
                                                                        <span className="hr-upload-cell-data">{row.student_name || '-'}</span>
                                                                    )}
                                                                </td>
                                                                <td className="px-4">
                                                                    {row.isEditing ? (
                                                                        <input
                                                                            type="text"
                                                                            value={row.college_name || ''}
                                                                            onChange={(e) => updateManualRow(idx, 'college_name', e.target.value)}
                                                                            placeholder="Batch Name"
                                                                            className="hr-upload-cell-input w-full p-2.5 bg-white border border-slate-100 focus:border-red-400 focus:ring-2 focus:ring-red-50 rounded-xl outline-none transition-all shadow-sm"
                                                                        />
                                                                    ) : (
                                                                        <span className="hr-upload-cell-data truncate max-w-[200px] inline-block align-middle">
                                                                            {row.college_name || '-'}
                                                                        </span>
                                                                    )}
                                                                </td>
                                                                <td className="px-4">
                                                                    {row.isEditing ? (
                                                                        <input
                                                                            type="text"
                                                                            value={row.register_number}
                                                                            onChange={(e) => updateManualRow(idx, 'register_number', e.target.value)}
                                                                            placeholder="Register ID"
                                                                            className="hr-upload-cell-input w-full p-2.5 bg-white border border-slate-100 focus:border-red-400 focus:ring-2 focus:ring-red-50 rounded-xl outline-none transition-all shadow-sm"
                                                                        />
                                                                    ) : (
                                                                        <span className="hr-upload-cell-data">{row.register_number || '-'}</span>
                                                                    )}
                                                                </td>
                                                                 <td className="px-4">
                                                                    {row.isEditing ? (
                                                                        <div className="flex flex-col text-left">
                                                                            <input
                                                                                type="text"
                                                                                value={row.phone}
                                                                                onChange={(e) => updateManualRow(idx, 'phone', e.target.value)}
                                                                                placeholder="Mobile Number"
                                                                                className={`hr-upload-cell-input w-full p-2.5 bg-white border focus:ring-2 rounded-xl outline-none transition-all shadow-sm ${row.phoneError ? 'border-red-500 focus:border-red-500 focus:ring-red-100' : 'border-slate-100 focus:border-red-400 focus:ring-red-50'}`}
                                                                            />
                                                                            {row.phoneError && (
                                                                                <span className="text-[10px] font-bold block mt-1 pl-1 text-left" style={{ color: '#ef4444' }}>
                                                                                    {row.phoneError}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex flex-col text-left">
                                                                            <span className="hr-upload-cell-data">{row.phone || '-'}</span>
                                                                            {row.phoneError && (
                                                                                <span className="text-[10px] font-bold block mt-0.5 text-left" style={{ color: '#ef4444' }}>
                                                                                    {row.phoneError}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td className="px-4">
                                                                    {row.isEditing ? (
                                                                        <div className="flex flex-col text-left">
                                                                            <input
                                                                                type="email"
                                                                                value={row.email}
                                                                                onChange={(e) => updateManualRow(idx, 'email', e.target.value)}
                                                                                placeholder="Email Address"
                                                                                className={`hr-upload-cell-input w-full p-2.5 bg-white border focus:ring-2 rounded-xl outline-none transition-all shadow-sm ${row.emailError ? 'border-red-500 focus:border-red-500 focus:ring-red-100' : 'border-slate-100 focus:border-red-400 focus:ring-red-50'}`}
                                                                            />
                                                                            {row.emailError && (
                                                                                <span className="text-[10px] font-bold block mt-1 pl-1 text-left" style={{ color: '#ef4444' }}>
                                                                                    {row.emailError}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex flex-col text-left">
                                                                            <span className="hr-upload-cell-data">{row.email || row.username || '-'}</span>
                                                                            {row.emailError && (
                                                                                <span className="text-[10px] font-bold block mt-0.5 text-left" style={{ color: '#ef4444' }}>
                                                                                    {row.emailError}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                {showCredentialColumns && (
                                                                    <>
                                                                        <td className="px-4 bg-red-50/20">
                                                                            <span className="hr-upload-cell-data">{row.username || row.email || '-'}</span>
                                                                        </td>
                                                                        <td className="px-4 bg-red-50/20">
                                                                            <span className="hr-upload-password-pill px-2.5 py-1 bg-red-50 rounded-lg border border-red-200 shadow-2xs inline-block">
                                                                                {row.password || '-'}
                                                                            </span>
                                                                        </td>
                                                                    </>
                                                                )}
                                                                <td className="px-4 text-center">
                                                                    {row.isEditing ? (
                                                                        <div className="flex items-center justify-center gap-2">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => saveManualRow(idx)}
                                                                                title="Save"
                                                                                className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-500 hover:text-white border border-red-100 shadow-sm active:scale-95 transition-all"
                                                                            >
                                                                                <FiCheck className="text-sm" />
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => cancelManualRow(idx)}
                                                                                title="Cancel"
                                                                                className="w-8 h-8 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center hover:bg-slate-200 border border-slate-100 shadow-sm active:scale-95 transition-all"
                                                                            >
                                                                                <FiX className="text-sm" />
                                                                            </button>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex items-center justify-center gap-2">
                                                                            <button
                                                                                onClick={() => startEditManualRow(idx)}
                                                                                title="Edit"
                                                                                className="w-8 h-8 rounded-xl bg-slate-50 text-slate-400 flex items-center justify-center hover:bg-blue-50 hover:text-blue-600 shadow-sm active:scale-95 transition-all"
                                                                            >
                                                                                <AssessmentEditIcon className="w-3.5 h-3.5" />
                                                                            </button>
                                                                            <button
                                                                                onClick={() => deleteManualRowRecord(idx, row.id)}
                                                                                title="Delete"
                                                                                className="w-8 h-8 rounded-xl bg-slate-50 text-slate-400 flex items-center justify-center hover:bg-red-50 hover:text-red-600 shadow-sm active:scale-95 transition-all"
                                                                            >
                                                                                <FiTrash2 className="text-sm" />
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="border border-slate-100 rounded-[28px] overflow-hidden shadow-sm animate-in fade-in duration-300 flex flex-col bg-white mb-6">
                                            <div className="hr-upload-empty-cols bg-slate-50/80 px-8 py-5 border-b border-slate-100 grid grid-cols-4 gap-4 text-left text-xs font-black text-slate-400 uppercase tracking-widest">
                                                <span>#</span>
                                                <span>Candidate Name</span>
                                                <span>Email ID / Username</span>
                                                <span>Register Number</span>
                                            </div>
                                            <div className="flex flex-col items-center justify-center py-28 text-center bg-white">
                                                <div className="w-20 h-20 bg-slate-50 rounded-3xl flex items-center justify-center text-slate-300 text-4xl mb-5 shadow-inner">
                                                    <FiInbox />
                                                </div>
                                                <h3 className="text-sm font-black tracking-[0.2em] uppercase text-slate-300">
                                                    No Candidates Found
                                                </h3>
                                                <p className="text-sm text-slate-400 font-bold mt-2 max-w-sm">
                                                    Click "+ Add Candidate" on top right to start adding manual candidates or upload an Excel roster.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="hr-upload-save-fixed flex items-center justify-end">
                    <button
                        type="button"
                        onClick={handleSave}
                        className="btn-assessment btn-assessment-danger"
                    >
                        Save
                    </button>
                </div>
            </div>

            <ExportReportColumnModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                columns={availableExportColumns}
                selectedColumnIds={selectedColumnIds}
                onToggleColumn={handleToggleColumn}
                onToggleSelectAll={handleToggleSelectAllColumns}
                onExportExcel={executeExportReportExcel}
                onExportPdf={executeExportReportPdf}
            />

            {/* Custom Delete Confirmation Modal */}
            {deleteConfirm.isOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-200">
                    <div className="bg-white rounded-[32px] max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 flex flex-col space-y-6 text-center relative overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="mx-auto w-16 h-16 bg-red-50 text-red-600 rounded-2xl flex items-center justify-center text-3xl shadow-inner mb-2">
                            <FiTrash2 />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase mb-3">Confirm Deletion</h3>
                            <p className="text-sm text-slate-500 font-bold leading-relaxed">{deleteConfirm.message}</p>
                        </div>
                        <div className="flex items-center justify-center gap-4 pt-4 border-t border-slate-100">
                            <button
                                onClick={() => setDeleteConfirm({ isOpen: false, type: null, data: null, message: '' })}
                                className="px-6 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-600 font-black text-xs uppercase tracking-wider rounded-2xl transition-all active:scale-95 w-full cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmDelete}
                                disabled={loading}
                                className="px-6 py-3.5 bg-red-600 hover:bg-red-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg shadow-red-200 transition-all active:scale-95 w-full flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? 'Deleting...' : 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default HRUploadManager;
